import { OPERATION_IDS, ROUTE_IDS } from '../routes/ids.js';
import { getRouteById } from '../routes/effective-routes.js';
import { ORGANIZATION_TABLE } from './table-registry.js';
function editPath() {
    const route = getRouteById(ROUTE_IDS.PLATFORM_ORGANIZATIONS_EDIT);
    if (!route)
        throw new Error('Platform organization edit route is missing');
    return route.path.replace(/:[A-Za-z0-9_-]+/, ':id');
}
function metadata(id) {
    return {
        id,
        schemaVersion: ORGANIZATION_TABLE.schemaVersion,
        module: ORGANIZATION_TABLE.module,
        feature: ORGANIZATION_TABLE.feature,
        privacyClassification: ORGANIZATION_TABLE.privacyClassification,
        telemetryId: `${String(ORGANIZATION_TABLE.telemetryId)}.${id}`,
        accessibilityLabelKey: 'action.edit',
        availability: 'AVAILABLE',
        owner: 'experience-registry',
    };
}
const EDIT_ACTION = {
    ...metadata('action.platformOrganization.edit'),
    operationId: OPERATION_IDS.PLATFORM_ORGANIZATIONS_UPDATE,
    labelKey: 'action.edit',
    iconKey: 'edit',
    kind: 'navigate',
    intent: 'primary',
    idempotencyRequired: false,
    successBehavior: 'NAVIGATE_TO',
    targetRouteId: ROUTE_IDS.PLATFORM_ORGANIZATIONS_EDIT,
    successNavigateTo: editPath(),
    errorBehavior: 'SHOW_INLINE_ERROR',
    order: 10,
    group: 'navigation',
};
const DISABLE_ACTION = {
    ...metadata('action.platformOrganization.disable'),
    accessibilityLabelKey: 'action.disable',
    operationId: OPERATION_IDS.PLATFORM_ORGANIZATIONS_DISABLE,
    labelKey: 'action.disable',
    iconKey: 'block',
    kind: 'command',
    method: 'POST',
    commandId: 'cmd.admin.platform-organizations.disable',
    intent: 'danger',
    idempotencyRequired: true,
    confirmation: {
        titleKey: 'common:confirmAction',
        messageKey: 'common:confirmAction',
        confirmLabelKey: 'action.disable',
        cancelLabelKey: 'action.cancel',
        fields: [
            {
                name: 'reason',
                labelKey: 'form.platformOrganization.disableReason',
                fieldType: 'text',
                required: true,
            },
            {
                name: 'approved',
                labelKey: 'form.platformOrganization.disableApproval',
                fieldType: 'checkbox',
                required: true,
            },
        ],
    },
    successBehavior: 'REFRESH_TABLE',
    successMessageKey: 'common:success',
    errorBehavior: 'SHOW_INLINE_ERROR',
    errorMessageKey: 'common:error.unexpected',
    // The canonical platform organization is required to retain the platform
    // control plane. The API independently rejects its disable command.
    visibilityCondition: { operator: 'notEquals', field: 'code', value: 'PLATFORM' },
    order: 20,
    group: 'danger',
    payloadFields: ['reason', 'approved'],
    payloadSchemaRef: 'platform-organization-disable',
    invalidationTargets: ['admin.organization'],
};
const SUSPEND_ACTION = {
    ...metadata('action.platformOrganization.suspend'),
    operationId: OPERATION_IDS.PLATFORM_ORGANIZATIONS_DISABLE,
    labelKey: 'action.suspend',
    iconKey: 'block',
    kind: 'command',
    method: 'POST',
    commandId: 'cmd.admin.platform-organizations.suspend',
    intent: 'danger',
    idempotencyRequired: true,
    confirmation: { ...DISABLE_ACTION.confirmation, confirmLabelKey: 'action.suspend' },
    successBehavior: 'REFRESH_TABLE',
    successMessageKey: 'common:success',
    errorBehavior: 'SHOW_INLINE_ERROR',
    errorMessageKey: 'common:error.unexpected',
    visibilityCondition: { operator: 'equals', field: 'status', value: 'ACTIVE' },
    order: 20,
    group: 'danger',
    payloadFields: ['reason', 'approved'],
    payloadSchemaRef: 'platform-organization-suspend',
    invalidationTargets: ['admin.organization'],
};
const REACTIVATE_ACTION = {
    ...metadata('action.platformOrganization.reactivate'),
    operationId: OPERATION_IDS.PLATFORM_ORGANIZATIONS_DISABLE,
    labelKey: 'action.reactivate',
    iconKey: 'refresh',
    kind: 'command',
    method: 'POST',
    commandId: 'cmd.admin.platform-organizations.reactivate',
    intent: 'secondary',
    idempotencyRequired: true,
    confirmation: { ...DISABLE_ACTION.confirmation, confirmLabelKey: 'action.reactivate' },
    successBehavior: 'REFRESH_TABLE',
    successMessageKey: 'common:success',
    errorBehavior: 'SHOW_INLINE_ERROR',
    errorMessageKey: 'common:error.unexpected',
    visibilityCondition: { operator: 'equals', field: 'status', value: 'SUSPENDED' },
    order: 21,
    group: 'secondary',
    payloadSchemaRef: 'no-payload',
    payloadFields: [],
    invalidationTargets: ['admin.organization'],
};
export const PLATFORM_ORGANIZATIONS_TABLE = Object.freeze({
    ...ORGANIZATION_TABLE,
    id: 'table.admin.platformOrganizations',
    rowActions: [EDIT_ACTION, SUSPEND_ACTION, REACTIVATE_ACTION, DISABLE_ACTION],
});
